/* Pocket Factory · EXP-6: the "cards shipped" number's live count. The banner above the header on every page says it
   (snippets/pf-ship-banner.liquid, [data-pf-cards-shipped]), from the theme setting pf_cards_count; since Nov 3
   (Bryton) it is the only line that does (the homepage's counter, [data-pf-cards-counter], is gone; one still drawn is
   updated the same way). With "Live count is
   deployed" on, this file asks the Worker once for the count it keeps (GET /v1/stats/cards-shipped, row EXP-6b: 180
   cards sold before the store plus the cards of paid orders marked fulfilled, cached there about 10 minutes). Its
   cards_shipped counts only when:
   - it answers within 1.5 s, and
   - it is a whole number at least the setting's (a cached count from before the setting was raised never lowers it).
   LONGRUN-1102 (Bryton, Nov 3: "a dash for each digit ... so that we're not showing people 200 and then 260"): a line
   changes its number at most once a page, and only from dashes. snippets/pf-cards-shipped-paint.liquid paints each
   line before it is first seen: the count this browser was given in the last 10 minutes, at once, or one dash per
   digit. This file then lands one number on every line, the same on both:
   - the number a line already shows (that last count), which stays;
   - else the Worker's count as above, which is also kept (localStorage pf.shipped.v1) for the next page;
   - else (no Worker, no answer in time, an error, a smaller or odd number) the last count the store's Worker gave this
     browser, or the setting's.
   Wherever the store's own Worker is the one to ask (the live store, a preview without ?pf_worker=), the request starts
   earlier, in that snippet (PocketShipped.ask), which lands an answer that comes within 1.5 s of the banner itself;
   this file reads that same answer (one request a page) and lands it when it comes within 1.5 s of this file running.
   It asks itself only for another Worker (?pf_worker= on a preview, a local host). An answer later than both is not
   shown on this page but is kept for the next one. Only the Worker's own counts are kept, never a fallback. The count
   can go down (an order cancelled or returned): the next page shows it.
   Each waiting digit rises out of its dash (transform and opacity; the whole number at once with reduced motion),
   then the number is plain text again. Never a "+": the number shown is exactly the setting's or one the Worker
   gave. One request per page: the banner brings this file on every page. */
(() => {
  'use strict';
  if (typeof document === 'undefined') return;
  const doc = document, win = window;
  const KEY = 'pf.shipped.v1', WAIT = 1500, MOST = 10000000;
  // The digits rise (pf-cards-shipped-paint's styles: 0.34 s each, 40 ms apart) unless the visitor wants less motion.
  const moving = win.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? true;
  const lines = [...doc.querySelectorAll('[data-pf-cards-counter],[data-pf-cards-shipped]')]
    .filter((line) => !line.dataset.pfCardsLive)
    .map((line) => ({ line, number: line.querySelector('[data-pf-cards-counter-n],[data-pf-cards-shipped-n]') }))
    .filter(({ number }) => number);
  if (!lines.length) return;
  for (const { line } of lines) line.dataset.pfCardsLive = '1';
  // Every line is drawn from the same setting; the smallest is the floor a live count must reach.
  const counts = lines.map(({ line }) => Number(line.dataset.count)).filter((n) => Number.isInteger(n) && n > 0);
  const drawn = Math.min(...counts);
  const valid = (count) => Number.isInteger(count) && count >= drawn && count < MOST;
  const kept = lines.map(({ line }) => Number(line.dataset.pfCardsKept)).find(valid);
  // The last count the store's Worker gave this browser (any age): a real count, shown rather than the setting's when
  // the Worker cannot answer now, so a returning visitor never sees it fall back.
  let last;
  try {
    const was = JSON.parse(win.localStorage.getItem(KEY));
    if (valid(was?.n)) last = was.n;
  } catch (_) {}
  const own = () => String(doc.body?.dataset.referralWorker || '').replace(/\/$/, '');
  /** Keep a count the store's own Worker gave for the next page (never a fallback, never a preview Worker's). */
  const keep = (n) => {
    try {
      win.localStorage.setItem(KEY, JSON.stringify({ n, t: Date.now() }));
    } catch (_) {}
  };
  /** A number as slots: each digit in its own, rising out of the dash it replaces; commas as text. */
  const slots = (words) => {
    let i = 0;
    return [...words].map((char) => {
      if (!/\d/.test(char)) return doc.createTextNode(char);
      const slot = doc.createElement('span'), digit = doc.createElement('span');
      slot.className = 'pf-csn__slot';
      slot.style.setProperty('--pf-csn-i', String(i++));
      digit.textContent = char;
      slot.append(digit);
      return slot;
    });
  };
  let landed = false, base = '';
  /** One number on every line, once. */
  const land = (count) => {
    if (landed) return;
    landed = true;
    const shown = kept ?? (valid(count) ? count : last ?? drawn);
    if (!Number.isInteger(shown)) return;
    const words = shown.toLocaleString('en-US');
    for (const { line, number } of lines) {
      line.dataset.count = String(shown);
      if (line.hasAttribute('data-pf-cards-wait')) {
        // Once the digits have risen the number is plain text again, so a screen reader reads "1,204", not each slot;
        // until then the line stays hidden from it, as it was while waiting.
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          number.textContent = words;
          line.removeAttribute('aria-hidden');
        };
        try {
          if (!moving) throw 0;
          number.replaceChildren(...slots(words));
          const tail = [...number.querySelectorAll('.pf-csn__slot > span')].pop();
          tail?.addEventListener('animationend', settle, { once: true });
          win.setTimeout(settle, 1500); // a hidden tab runs no animation
        } catch (_) {
          settle();
        }
        line.removeAttribute('data-pf-cards-wait');
      } else if (number.textContent.trim() !== words) number.textContent = words;
    }
  };
  if (!Number.isInteger(drawn) || typeof win.fetch !== 'function') return land(null);
  // pocket-core.js names the Worker (a preview's own, or production); the layout's data-referral-worker is the
  // fallback.
  base = String(win.PocketCore?.workerBase?.() || doc.body?.dataset.referralWorker || '').replace(/\/$/, '');
  if (!/^https:\/\/[^/]+$/.test(base)) return land(null);
  // Past 1.5 s the page shows what it has; the answer is still read when it comes, and kept for the next page.
  const timer = win.setTimeout(() => land(null), WAIT);
  const asked = { headers: { Accept: 'application/json' }, credentials: 'omit' };
  // The snippet's early request on the live store (one request a page), else this file's own.
  const answer = win.PocketShipped?.ask || win.fetch(base + '/v1/stats/cards-shipped', asked).then((response) => (response.ok ? response.json() : null));
  answer
    .then((body) => {
      const count = body?.cards_shipped;
      if (!valid(count)) return land(null);
      // Only the store's own Worker's count is kept for the next page (never a preview's ?pf_worker=).
      if (base === own()) keep(count);
      land(count);
    })
    .catch(() => land(null))
    .finally(() => win.clearTimeout(timer));
})();
